/** Apply a chosen mutation card to the board */

import type { PRNG } from '../rng.js';
import type { BoardState, MutationCard } from '../types.js';
import { MutationType } from './registry.js';
import { applyColorShift } from './types/color-shift.js';
import { applyGravityNudge } from './types/gravity-nudge.js';
import { applyObstacleSpawn } from './types/obstacle-spawn.js';
import { applyRainbowRow } from './types/rainbow-row.js';
import { applyScoreFrenzy } from './types/score-frenzy.js';
import { applyBubbleBomb } from './types/bubble-bomb.js';

/**
 * Apply a mutation card to the board state.
 * Dispatches to the appropriate handler based on card type.
 * Returns a new board state (never mutates the input).
 */
export function applyMutation(
  board: BoardState,
  card: MutationCard,
  rng: PRNG,
): BoardState {
  switch (card.type) {
    case MutationType.RowInject:
      // handled in engine.ts as a one-row descent jump (applyHarmfulCard)
      return board;
    case MutationType.ColorShift:
      return applyColorShift(board, card, rng);
    case MutationType.GravityNudge:
      return applyGravityNudge(board, card, rng);
    case MutationType.ObstacleSpawn:
      return applyObstacleSpawn(board, card, rng);
    case MutationType.RainbowRow:
      return applyRainbowRow(board, card, rng);
    case MutationType.ScoreFrenzy:
      return applyScoreFrenzy(board, card, rng);
    case MutationType.BubbleBomb:
      return applyBubbleBomb(board, card, rng);
    case MutationType.SlowClockwork:
      // handled via ActiveMutation in engine.ts
      return board;
    case MutationType.CascadeCoil:
      // handled via ActiveMutation in engine.ts
      return board;
    case MutationType.WildcardGear:
      // handled via ActiveMutation in engine.ts
      return board;
    case MutationType.NarrowSpectrum:
      // handled via ActiveMutation in engine.ts
      return board;
    case MutationType.LubricatedBearings:
      // handled via ActiveMutation in engine.ts
      return board;
    case MutationType.GovernorLock:
      // handled via ActiveMutation in engine.ts
      return board;
    default:
      return board;
  }
}
